export const PROJECT_COLOR_OPTIONS = [
    { id: "cobalt", label: "Cobalt", value: "var(--color-blue-700)" },
    { id: "orange", label: "Orange", value: "var(--color-orange-500)" },
    { id: "emerald", label: "Emerald", value: "var(--color-emerald-500)" },
    { id: "rose", label: "Rose", value: "var(--color-rose-500)" },
    { id: "violet", label: "Violet", value: "var(--color-violet-500)" },
    { id: "sky", label: "Sky", value: "var(--color-sky-500)" },
    { id: "yellow", label: "Yellow", value: "var(--color-yellow-500)" },
    { id: "magenta", label: "Magenta", value: "var(--color-fuchsia-700)" },
    { id: "teal", label: "Teal", value: "var(--color-teal-500)" },
    { id: "cyan", label: "Cyan", value: "var(--color-cyan-500)" },
    { id: "red", label: "Red", value: "var(--color-red-500)" },
    { id: "fuchsia", label: "Fuchsia", value: "var(--color-fuchsia-500)" },
    { id: "blue", label: "Blue", value: "var(--color-blue-500)" },
    { id: "green", label: "Green", value: "var(--color-green-500)" },
    { id: "purple", label: "Purple", value: "var(--color-purple-500)" },
    { id: "coral", label: "Coral", value: "var(--color-orange-400)" },
    { id: "lime", label: "Lime", value: "var(--color-lime-500)" },
    { id: "indigo", label: "Indigo", value: "var(--color-indigo-500)" },
    { id: "pink", label: "Pink", value: "var(--color-pink-500)" },
    { id: "amber", label: "Amber", value: "var(--color-amber-500)" },
] as const

export type ProjectColor = (typeof PROJECT_COLOR_OPTIONS)[number]["id"]

export interface ProjectIdentityFields {
    projectChars?: string
    projectEmoji?: string
}

export interface ProjectIdentity {
    projectChars: string
    projectEmoji: string
}

interface ProjectAppearanceSource {
    id: string
    color?: ProjectColor
}

const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" })

function graphemes(value: string): string[] {
    return Array.from(graphemeSegmenter.segment(value), ({ segment }) => segment)
}

export function normalizeProjectChars(value: string): string {
    return graphemes(value.trim().toUpperCase().normalize("NFC"))
        .filter((char) => /^[\p{L}\p{N}]\p{M}*$/u.test(char))
        .slice(0, 2)
        .join("")
}

export function parseProjectChars(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined
    const chars = normalizeProjectChars(value)
    return graphemes(chars).length === 2 ? chars : undefined
}

export function normalizeProjectEmoji(value: string): string {
    const parts = graphemes(value.trim())
    return parts.length === 1 && /[\p{Extended_Pictographic}\p{Regional_Indicator}\u20e3]/u.test(parts[0]) ? parts[0] : ""
}

export function defaultProjectChars(name: string): string {
    const words = name
        .replace(/([\p{Ll}\d])(\p{Lu})/gu, "$1 $2")
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean)
    const chars =
        words.length > 1
            ? words
                  .slice(0, 2)
                  .map((word) => graphemes(word)[0])
                  .join("")
            : (words[0] ?? "")
    return normalizeProjectChars(`${chars}XX`)
}

export function projectIdentity(project: ProjectIdentityFields & { name: string; glyph?: string }): ProjectIdentity {
    return {
        projectChars: parseProjectChars(project.projectChars) || parseProjectChars(project.glyph) || defaultProjectChars(project.name),
        projectEmoji: normalizeProjectEmoji(project.projectEmoji ?? "") || normalizeProjectEmoji(project.glyph ?? ""),
    }
}

function automaticProjectColorIndex(projectId: string): number {
    let hash = 0
    for (const character of projectId) {
        hash = (Math.imul(hash, 31) + character.charCodeAt(0)) >>> 0
    }
    return hash % PROJECT_COLOR_OPTIONS.length
}

export function isProjectColor(value: unknown): value is ProjectColor {
    return PROJECT_COLOR_OPTIONS.some((option) => option.id === value)
}

export function projectColorValue(color: ProjectColor): string {
    return PROJECT_COLOR_OPTIONS.find((option) => option.id === color)?.value ?? PROJECT_COLOR_OPTIONS[0].value
}

export function automaticProjectColor(projectId: string): string {
    return PROJECT_COLOR_OPTIONS[automaticProjectColorIndex(projectId)].value
}

export function projectColorAssignments(projects: readonly ProjectAppearanceSource[]): ReadonlyMap<string, string> {
    const uniqueProjects = new Map<string, ProjectAppearanceSource>()
    for (const project of projects) uniqueProjects.set(project.id, project)

    const colorByProjectId = new Map<string, string>()
    const usedColorIndexes = new Set<number>()
    const sortedProjects = [...uniqueProjects.values()].sort((a, b) => a.id.localeCompare(b.id))

    for (const project of sortedProjects) {
        if (!project.color) continue
        const colorIndex = PROJECT_COLOR_OPTIONS.findIndex((option) => option.id === project.color)
        colorByProjectId.set(project.id, PROJECT_COLOR_OPTIONS[colorIndex].value)
        usedColorIndexes.add(colorIndex)
    }

    for (const project of sortedProjects) {
        if (project.color) continue
        const preferredIndex = automaticProjectColorIndex(project.id)
        let colorIndex = preferredIndex

        if (usedColorIndexes.size < PROJECT_COLOR_OPTIONS.length) {
            while (usedColorIndexes.has(colorIndex)) {
                colorIndex = (colorIndex + 1) % PROJECT_COLOR_OPTIONS.length
            }
            usedColorIndexes.add(colorIndex)
        }

        colorByProjectId.set(project.id, PROJECT_COLOR_OPTIONS[colorIndex].value)
    }

    return colorByProjectId
}

export function normalizeProjectGlyph(value: string): string {
    return graphemes(value.trim()).slice(0, 2).join("")
}
